import Stack from '@mui/material/Stack';
import { useTheme, SxProps, Theme } from '@mui/material';
import { useMemo, ReactNode } from 'react';
import type { RemixiconComponentType } from '@remixicon/react';

import type {
    DesignSystemEmptyStateType,
    DesignSystemEmptyStateSize,
} from '../../theme/design-system/types';
import type { Slots } from '../../utils/Slots';
import { SlotProvider } from '../../utils/SlotContext';
import { Typography } from '../Typography/Typography';
import { Card } from '../Card/Card';
import { EmptyStateIcon } from './EmptyStateIcon';
import { EmptyStateAction } from './EmptyStateAction';
import {
    sizeToCardSize,
    contentGapMap,
    titleVariantMap,
    descriptionVariantMap,
    getTypeStyles,
} from './EmptyState.styleUtils';

export type EmptyStateProps = {
    icon: RemixiconComponentType;
    title: ReactNode;
    description?: ReactNode;
    type?: DesignSystemEmptyStateType;
    size?: DesignSystemEmptyStateSize;
    noBorder?: boolean;
    children?: ReactNode;
    sx?: SxProps<Theme>;
};

const EmptyStateBase = ({
    icon,
    title,
    description,
    type = 'default',
    size = 'md',
    noBorder = false,
    children,
    sx,
}: EmptyStateProps) => {
    const theme = useTheme();
    const cardSize = sizeToCardSize[size];
    const typeStyles = getTypeStyles(type, theme);
    const titleVariant = titleVariantMap[size];
    const descriptionVariant = descriptionVariantMap[size];

    const buttonSlots: Slots = useMemo(
        () => ({ button: { size: cardSize } }),
        [cardSize],
    );

    return (
        <Card
            direction="vertical"
            size={cardSize}
            sx={{
                alignItems: 'center',
                gap: contentGapMap[size],
                ...(size === 'xl' && { width: '100%' }),
                ...typeStyles,
                ...(noBorder && { border: 'none' }),
                ...sx,
            }}
        >
            <EmptyStateIcon icon={icon} size={size} type={type} />
            <Stack gap={0.25} alignItems="center">
                <Typography
                    variant={titleVariant}
                    fontWeight="semibold"
                    sx={{ textAlign: 'center', whiteSpace: 'pre-line' }}
                >
                    {title}
                </Typography>
                {description && (
                    <Typography
                        variant={descriptionVariant}
                        color="ds.fg.neutral.primary"
                        textAlign="center"
                        balance
                    >
                        {description}
                    </Typography>
                )}
            </Stack>
            {children && (
                <SlotProvider value={buttonSlots}>{children}</SlotProvider>
            )}
        </Card>
    );
};

export const EmptyState = Object.assign(EmptyStateBase, {
    Action: EmptyStateAction,
});
